import { useEffect, useRef } from 'react';

interface PerformanceMetrics {
  renderCount: number;
  lastRenderTime: number;
  averageRenderTime: number;
}

const performanceData = new Map<string, PerformanceMetrics>();

export const usePerformanceMonitor = (componentName: string, enabled: boolean = process.env.NODE_ENV === 'development') => {
  const renderCountRef = useRef(0);
  const metricsRef = useRef<PerformanceMetrics | undefined>();
  const renderStartTimeRef = useRef(0);

  // Always call hooks in the same order
  useEffect(() => {
    if (!enabled) return;

    const renderEndTime = performance.now();
    const renderTime = renderEndTime - renderStartTimeRef.current;
    
    const existing = performanceData.get(componentName) || {
      renderCount: 0,
      lastRenderTime: 0,
      averageRenderTime: 0,
    };

    const newMetrics: PerformanceMetrics = {
      renderCount: existing.renderCount + 1,
      lastRenderTime: renderTime,
      averageRenderTime: (existing.averageRenderTime * existing.renderCount + renderTime) / (existing.renderCount + 1),
    };

    performanceData.set(componentName, newMetrics);
    metricsRef.current = newMetrics;

    // Log performance data every 10 renders
    if (newMetrics.renderCount % 10 === 0) {
      console.log(`🚀 Performance [${componentName}]:`, {
        renders: newMetrics.renderCount,
        lastRender: `${newMetrics.lastRenderTime.toFixed(2)}ms`,
        avgRender: `${newMetrics.averageRenderTime.toFixed(2)}ms`,
      });
    }
  });

  // Track render start time and count
  if (enabled) {
    renderStartTimeRef.current = performance.now();
    renderCountRef.current += 1;
  }

  return {
    renderCount: renderCountRef.current,
    getMetrics: () => metricsRef.current || performanceData.get(componentName),
  };
};

// Utility function to get all performance data
export const getAllPerformanceMetrics = () => {
  const metrics: Record<string, PerformanceMetrics> = {};
  performanceData.forEach((value, key) => {
    metrics[key] = value;
  });
  return metrics;
};

// Utility function to reset performance data
export const resetPerformanceMetrics = () => {
  performanceData.clear();
}; 